import { LocationState } from '@common/state/location/state'
import { getLocationState } from '@common/state/selectors'
import { BuyCheckoutState } from '@buy/state/checkout/state'
import {
    getCheckoutState,
    getContentData,
    getCouponState,
    getPlanData,
    getProductSettingsPlatformProduct,
    getUserState,
} from '@buy/state/selectors'
import { BuyUserState } from '@buy/state/user/state'
import { isEmpty } from 'lodash-es'
import { select } from 'redux-saga/effects'
import { BuyPlan } from '@buy/state/plan'
import { BuyCouponState } from '@buy/state/coupon/state'
import { BuyCustomizeItemsData } from '@buy/state/tracking/state'
import { BuyContentData } from '@buy/state/content-data'
import { BillingPeriodKeyMapper, BillingPeriodTimePeriodMapper } from '@common/constants'
import { getPriceTier } from '@buy/utils/volume-components-helpers'

export function* getCustomizeItemsData() {
    const planData: BuyPlan = yield select(getPlanData)
    const { CurrencyCode, marketoProductName }: BuyContentData = yield select(getContentData)
    const { billingTenure, organizers, baseLine, additionalLines, addonLines }: BuyCheckoutState = yield select(
        getCheckoutState
    )
    const customizeItemsData: BuyCustomizeItemsData = {
        planData,
        billingFrequency: BillingPeriodTimePeriodMapper[billingTenure.selected],
        currency: CurrencyCode,
        organizers,
        productFamilyName: marketoProductName,
        baseLine,
        additionalLines,
        addonLines,
    }
    return customizeItemsData
}

export function* getPageViewData() {
    const {
        loggedIn: { isUserLoggedIn },
    }: BuyUserState = yield select(getUserState)
    const locationState: LocationState = yield select(getLocationState)
    const { locale, country, language } = locationState.locationInfo
    return { locale, country, isUserLoggedIn, language }
}

export function* getSessionInfo() {
    const {
        userType,
        loggedIn: { isUserLoggedIn },
    }: BuyUserState = yield select(getUserState)
    return { isUserLoggedIn, userType, sessionLast: '', sessionNumber: '' }
}

export function* getCartInfo() {
    const { billingTenure, baseLine, additionalLines, addonLines, totalDiscount }: BuyCheckoutState = yield select(
        getCheckoutState
    )
    const { marketoProductName }: BuyContentData = yield select(getContentData)
    const plan: BuyPlan = yield select(getPlanData)
    const billingPeriodKeys = plan.billingPeriods.map((item) => {
        return BillingPeriodKeyMapper[item.key]
    })
    const locationState: LocationState = yield select(getLocationState)
    const { existingBillingInformation }: BuyUserState = yield select(getUserState)
    const { currency } = locationState.locationInfo
    const tierIndex = getPriceTier(baseLine.multiplier, baseLine.allPrices[currency])

    const coupon: BuyCouponState = yield select(getCouponState)
    const { validCoupons, couponServerData } = coupon
    const totalCouponSavings = validCoupons.reduce((sum, currentValue) => {
        if (!currentValue.isReprice) return sum + currentValue.discount
        return sum
    }, 0)

    return {
        plan,
        currency,
        isExistingUser: !isEmpty(existingBillingInformation),
        tier: `Tier${tierIndex + 1}`,
        couponCode: couponServerData?.couponCode || '',
        validCoupons: couponServerData?.isCouponValid ? validCoupons : [],
        totalCouponSavings,
        billingPeriod: billingTenure.selected,
        addonLines,
        additionalLines,
        baseLine,
        billingPeriodKeys,
        totalDiscount,
        productFamilyName: marketoProductName,
    }
}

export function* getPurchaseData(transactionID: string) {
    const { calculatedNetTotal, calculatedTax, promoCode }: BuyCheckoutState = yield select(getCheckoutState)
    const { userType }: BuyUserState = yield select(getUserState)
    return { calculatedNetTotal, calculatedTax, promoCode, userType, transactionID }
}

export function* getCompleteBuyData() {
    const { userType }: BuyUserState = yield select(getUserState)
    return userType
}

export function* getPlatformProduct() {
    const platformProduct: string = yield select(getProductSettingsPlatformProduct)
    return platformProduct
}
